import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { playSpeechStream } from '@evi/ui';
import { voiceOptionsForLocale, normalizeLocale } from '@evi/shared';
import { EvieOrb } from '@/components/primitives';
import { useAuthStore } from '@/store/auth';
import { useEvieVoiceStore } from '@/store/evieVoice';
import { fetchBirthday, synthesizeDriverSpeech } from '@/lib/api';
import { useBirthdayCelebration } from '@/store/birthdayCelebration';
import styles from './BirthdayCelebration.module.css';

// CSS-only confetti, copied from RedemptionSheet (no new dependency).
const CONFETTI_COLORS = ['#FACC15', '#22C55E', '#FB923C', '#60A5FA', '#F472B6'];
const CONFETTI_COUNT = 40;

function Confetti() {
  const particles = useMemo(
    () => Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
      left: Math.random() * 100,
      xDrift: (Math.random() - 0.5) * 40,
      rot: (Math.random() - 0.5) * 1080,
      dur: 2 + Math.random() * 1.2,
      delay: Math.random() * 0.6,
      bg: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      key: i,
    })),
    [],
  );
  return (
    <div className={styles.confettiLayer} aria-hidden>
      {particles.map((p) => (
        <span
          key={p.key}
          className={styles.confettiPiece}
          style={{
            ['--left' as string]: String(p.left),
            ['--x' as string]: String(p.xDrift),
            ['--rot' as string]: `${p.rot}deg`,
            ['--dur' as string]: `${p.dur}s`,
            ['--delay' as string]: `${p.delay}s`,
            ['--bg' as string]: p.bg,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Birthday celebration overlay — one app-wide instance (mounted in AppShell,
 * portaled to body). Fires from the claim success and from a replay grant.
 * Confetti falls top-to-bottom; the EVII orb floats above with a warm birthday
 * message; then EVII SAYS "Happy Birthday" in the driver's selected language via
 * the existing /speak path (text-only locales like Armenian show the message
 * with no audio). Enjoy / Skip both close it — the points are already awarded.
 */
export function BirthdayCelebration() {
  const active = useBirthdayCelebration((s) => s.active);
  const points = useBirthdayCelebration((s) => s.points);
  const isLast = useBirthdayCelebration((s) => s.isLast);
  const dismiss = useBirthdayCelebration((s) => s.dismiss);
  const { t, i18n } = useTranslation('rewards');
  const locale = normalizeLocale(i18n.language);
  const userName = useAuthStore((s) => s.user?.name ?? null);
  const userId = useAuthStore((s) => s.user?._id ?? null);
  const voice = useEvieVoiceStore((s) => (userId && s.voiceByUser[userId]) || 'female');

  const birthdayQ = useQuery({
    queryKey: ['birthday'],
    queryFn: fetchBirthday,
    enabled: active,
    staleTime: 60_000,
  });

  const firstName = userName ? (userName.trim().split(/\s+/)[0] ?? t('celebration.friend')) : t('celebration.friend');
  const bd = birthdayQ.data?.birthday ?? null;
  const dateStr = bd
    ? new Date(2000, bd.month - 1, bd.day).toLocaleDateString(i18n.language, { month: 'long', day: 'numeric' })
    : null;

  // EVII speaks its line once per activation — best-effort. Text-only locales
  // (voiceOptionsForLocale empty, e.g. Armenian) skip audio; the text still shows.
  useEffect(() => {
    if (!active) return undefined;
    if (voiceOptionsForLocale(locale).length === 0) return undefined;
    let stop: (() => void) | null = null;
    let cancelled = false;
    void (async () => {
      const line = isLast
        ? t('celebration.spokenLast', { name: firstName })
        : t('celebration.spoken', { name: firstName });
      try {
        const res = await synthesizeDriverSpeech(line, voice, locale);
        if (cancelled) return;
        const playback = playSpeechStream(res);
        stop = playback.stop;
        await playback.done;
      } catch {
        /* muted / autoplay-blocked / synth error — the on-screen text remains */
      }
    })();
    return () => { cancelled = true; stop?.(); };
  }, [active, isLast, locale, voice, firstName, t]);

  if (!active) return null;

  return createPortal(
    <div
      className={styles.root}
      role="dialog"
      aria-modal="true"
      aria-label={t('celebration.greeting', { name: firstName })}
      onClick={dismiss}
    >
      <Confetti />
      <div className={styles.card} onClick={(e) => e.stopPropagation()}>
        <EvieOrb size={96} className={styles.orb} title="EVII" />
        <h2 className={styles.greeting}>{t('celebration.greeting', { name: firstName })}</h2>
        {dateStr && <p className={styles.dateLine}>{t('celebration.dateLine', { date: dateStr })}</p>}
        <p className={styles.affirmation}>{t('celebration.affirmation')}</p>
        <p className={styles.points}>{t('celebration.points', { points })}</p>
        {isLast && <p className={styles.lastNote}>{t('celebration.lastTime')}</p>}
        <div className={styles.actions}>
          <button type="button" className={styles.enjoyBtn} onClick={dismiss}>{t('celebration.enjoy')}</button>
          <button type="button" className={styles.skipBtn} onClick={dismiss}>{t('celebration.skip')}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
